import React, { useState } from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { MagneticEffect } from './MagneticEffect';
import { StarIcon } from './icons/StarIcon'; 
import { ChevronLeftIcon } from './icons/ChevronLeftIcon';
import { ChevronRightIcon } from './icons/ChevronRightIcon';
import { useTranslation } from '../i18n/LanguageContext';

interface Testimonial {
  name: string;
  relation: string; 
  text: string;
  stars: number;
  photoUrl?: string; 
}

const TestimonialCard: React.FC<Testimonial> = ({ name, relation, text, stars, photoUrl }) => (
  <SpotlightCard 
    className="bg-brand-primary/60 p-6 sm:p-8 rounded-lg shadow-xl flex flex-col h-full hover:shadow-brand-accent/20 transition-shadow duration-300"
  >
    {photoUrl && (
      <div className="flex justify-center mb-4">
        <MagneticEffect strength={30}>
          <img 
            src={photoUrl} 
            alt={`Foto de ${name}`} 
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover shadow-md" 
            loading="lazy"
          />
        </MagneticEffect>
      </div>
    )}
    <div className="flex justify-center sm:justify-start mb-3">
      {[...Array(5)].map((_, i) => (
        <StarIcon key={i} className={`w-5 h-5 ${i < stars ? 'text-brand-accent' : 'text-slate-500'}`} />
      ))}
    </div>
    <p className="text-brand-text mb-4 flex-grow italic text-center sm:text-left text-sm sm:text-base">"{text}"</p>
    <div className="text-center sm:text-left">
      <p className="font-semibold text-brand-light text-base sm:text-lg">{name}</p>
      <p className="text-sm text-slate-400">{relation}</p>
    </div>
  </SpotlightCard>
);

export const TextTestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials: Testimonial[] = t('textTestimonials.items');
  const testimonialsCount = testimonials.length;

  const nextTestimonial = () => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonialsCount);
  };
  const prevTestimonial = () => {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonialsCount) % testimonialsCount);
  };

  return (
    <section id="depoimentos-texto" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('textTestimonials.title')}
            subtitle={t('textTestimonials.subtitle')}
          />
        </ScrollAnimation>
        
        <ScrollAnimation animation="slideInUp" delay={200}>
          <div className="relative max-w-2xl mx-auto">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 p-1">
                    <TestimonialCard {...testimonial} />
                  </div>
                ))}
              </div>
            </div>

            {testimonials.length > 1 && (
              <>
                <button 
                  onClick={prevTestimonial}
                  className="absolute top-1/2 -translate-y-1/2 -left-4 sm:-left-6 md:-left-12 z-10 p-2 rounded-full bg-brand-primary/50 hover:bg-brand-primary/80 text-brand-light hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  aria-label={t('textTestimonials.prevAriaLabel')}
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <button 
                  onClick={nextTestimonial}
                  className="absolute top-1/2 -translate-y-1/2 -right-4 sm:-right-6 md:-right-12 z-10 p-2 rounded-full bg-brand-primary/50 hover:bg-brand-primary/80 text-brand-light hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  aria-label={t('textTestimonials.nextAriaLabel')}
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
};